import QRCode from 'qrcode';

/**
 * 二维码生成器类
 * 支持将URL生成二维码，并绘制到画布上
 */
export class QRCodeGenerator {
  constructor() {
    this.canvas = null;
    this.ctx = null;
  }

  /**
   * 创建画布
   * @param {number} size 画布大小，默认256px
   * @returns {HTMLCanvasElement} 画布元素
   */
  createCanvas(size = 256) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = size;
    this.canvas.height = size;
    this.ctx = this.canvas.getContext('2d');
    return this.canvas;
  }

  /**
   * 生成二维码并绘制到画布
   * @param {string} url 要生成二维码的URL
   * @param {Object} options 配置选项
   * @returns {Promise<HTMLCanvasElement>} 返回包含二维码的画布
   */
  async generateQRCode(url, options = {}) {
    const defaultOptions = {
      width: 237,
      height: 237,
      color: {
        dark: '#000000',  // 前景色
        light: '#FFFFFF' // 背景色
      },
      margin: 2,
      errorCorrectionLevel: 'M'
    };

    const config = { ...defaultOptions, ...options };
    
    if (!this.canvas) {
      this.createCanvas(config.width);
    }

    try {
      // 使用qrcode库生成二维码到画布
      await QRCode.toCanvas(this.canvas, url, config);
      return this.canvas;
    } catch (error) {
      console.error('生成二维码失败:', error);
      throw new Error('生成二维码失败');
    }
  }

  /**
   * 将画布转换为图片数据URL
   * @param {string} format 图片格式，默认'image/png'
   * @param {number} quality 图片质量 0-1，仅对jpeg有效
   * @returns {string} 图片的DataURL
   */
  toDataURL(format = 'image/png', quality = 1) {
    if (!this.canvas) {
      throw new Error('画布未初始化');
    }
    return this.canvas.toDataURL(format, quality);
  }

  /**
   * 将画布转换为Blob对象
   * @param {string} format 图片格式，默认'image/png'
   * @param {number} quality 图片质量 0-1
   * @returns {Promise<Blob>} 图片Blob对象
   */
  toBlob(format = 'image/png', quality = 1) {
    return new Promise((resolve, reject) => {
      if (!this.canvas) {
        reject(new Error('画布未初始化'));
        return;
      }
      
      this.canvas.toBlob(resolve, format, quality);
    });
  }

  /**
   * 下载二维码图片
   * @param {string} filename 文件名，默认'qrcode.png'
   * @param {string} format 图片格式，默认'image/png'
   */
  download(filename = 'qrcode.png', format = 'image/png') {
    if (!this.canvas) {
      throw new Error('画布未初始化');
    }

    const dataURL = this.toDataURL(format);
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * 清除画布
   */
  clear() {
    if (this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /**
   * 生成完整的邀请海报
   * @param {string} url 要生成二维码的URL
   * @param {Object} posterOptions 海报配置选项
   * @returns {Promise<string>} 返回海报的base64数据
   */
  async generateInvitePoster(url, posterOptions = {}) {
    const defaultOptions = {
      width: 334,
      height: 467,
      backgroundColor: '#ffffff',
      qrSize: 200,
      // 海报文案配置
      title: '欢迎体验金声玉亮',
      subtitle: '看清信息，稳住情绪\n让判断少一点干扰，多一点确定性',
      appName: 'Shanghai-JinShengYuLiang-202503260042',
      license: '网信算备310104666296401240017号'
    };

    const options = { ...defaultOptions, ...posterOptions };

    // 使用2倍分辨率提高画质
    const scale = 2;
    const scaledWidth = options.width * scale;
    const scaledHeight = options.height * scale;

    // 创建高分辨率海报画布
    const posterCanvas = document.createElement('canvas');
    posterCanvas.width = scaledWidth;
    posterCanvas.height = scaledHeight;
    
    // iOS兼容性：设置Canvas属性
    posterCanvas.style.backgroundColor = 'transparent';
    
    const posterCtx = posterCanvas.getContext('2d', { 
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: true,
      willReadFrequently: false,
      desynchronized: false 
    });
    
    // 确保画布背景透明 - iOS兼容处理
    posterCtx.clearRect(0, 0, scaledWidth, scaledHeight);
    posterCtx.globalCompositeOperation = 'source-over';
    
    // 缩放画布上下文以匹配高分辨率
    posterCtx.scale(scale, scale);
    
    // 优化文字渲染质量
    posterCtx.textRenderingOptimization = 'optimizeQuality';
    posterCtx.imageSmoothingEnabled = true;
    posterCtx.imageSmoothingQuality = 'high';

    // iOS兼容的透明背景处理
    posterCtx.save();
    
    // 创建裁剪路径，只保留圆角区域
    posterCtx.beginPath();
    this.drawRoundedRect(posterCtx, 0, 0, options.width, options.height, 32);
    posterCtx.clip();
    
    // 在裁剪区域内绘制渐变背景
    const gradient = posterCtx.createLinearGradient(0, options.height, options.width, 0);
    gradient.addColorStop(0, '#7E5C3E');
    gradient.addColorStop(0.5, '#B1916A');
    gradient.addColorStop(1, '#E4C6A4');
    posterCtx.fillStyle = gradient;
    posterCtx.fillRect(0, 0, options.width, options.height);
    
    posterCtx.restore();

    // 生成并绘制二维码
    await this.drawQRCode(posterCtx, url, options);

    // 绘制海报标题
    this.drawPosterTitle(posterCtx, options);

    // 设置CSS尺寸以确保显示时的正确大小
    posterCanvas.style.width = options.width + 'px';
    posterCanvas.style.height = options.height + 'px';

    // iOS兼容的PNG输出处理
    return await this.getCanvasDataURL(posterCanvas);
  }



  /**
   * 绘制海报标题
   */
  drawPosterTitle(ctx, options) {
    const centerX = options.width / 2;
    
    // 保存当前上下文状态
    ctx.save();
    
    // 优化文字渲染
    ctx.textRenderingOptimization = 'optimizeQuality';
    
    // 主标题："欢迎体验金声玉亮"
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 24px "PingFang SC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(options.title, centerX, 347); // 根据设计稿调整位置

    // 副标题
    ctx.font = '15px "PingFang SC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillStyle = '#FFFFFFCC';
    const subtitleLines = options.subtitle.split('\n');
    subtitleLines.forEach((line, index) => {
      ctx.fillText(line, centerX, 386 + index * 23); // 根据设计稿调整位置
    });
    
    // 恢复上下文状态
    ctx.restore();
  }

  /**
   * 绘制二维码
   */
  async drawQRCode(ctx, url, options) {
    // 使用高分辨率生成二维码以提高清晰度
    const qrScale = 2;
    const qrOptions = {
      width: options.qrSize * qrScale,
      height: options.qrSize * qrScale,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      margin: 1,
      errorCorrectionLevel: 'M'
    };

    await this.generateQRCode(url, qrOptions);
    
    // 计算二维码位置（居中，位于上部区域）
    const qrContainerSize = 250;
    const qrContainerX = (options.width - qrContainerSize) / 2;
    const qrContainerY = 42; // 根据设计稿调整位置
    
    // 绘制二维码容器背景
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    this.drawRoundedRect(ctx, qrContainerX, qrContainerY, qrContainerSize, qrContainerSize, 20);
    ctx.fill();
    ctx.restore();

    // 绘制二维码
    const qrX = qrContainerX + 25; // 25px内边距
    const qrY = qrContainerY + 25; // 25px内边距
    ctx.drawImage(this.canvas, qrX, qrY, options.qrSize, options.qrSize);
  }

  /**
   * iOS兼容的Canvas数据URL获取
   * @param {HTMLCanvasElement} canvas 画布元素
   * @returns {Promise<string>} DataURL
   */
  async getCanvasDataURL(canvas) {
    try {
      // 检测是否为iOS设备
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS) {
        // iOS特殊处理：使用toBlob方法确保透明背景
        return new Promise((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            } else {
              // fallback到标准方法
              resolve(canvas.toDataURL('image/png'));
            }
          }, 'image/png', 1.0);
        });
      } else {
        // 非iOS设备直接返回
        return canvas.toDataURL('image/png');
      }
    } catch (error) {
      console.error('获取Canvas DataURL时发生错误:', error);
      // fallback到标准方法
      return canvas.toDataURL('image/png');
    }
  }

  /**
   * 绘制底部信息
   */
  drawBottomInfo(ctx, options) {
    const centerX = options.width / 2;
    
    // 保存当前上下文状态
    ctx.save();
    
    // 优化文字渲染
    ctx.textRenderingOptimization = 'optimizeQuality';
    ctx.fillStyle = '#000000';
    ctx.font = '11px "PingFang SC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // 应用名称
    ctx.fillText(options.appName, centerX, 295);
    
    // 许可证号
    ctx.globalAlpha = 0.7;
    ctx.fillText(options.license, centerX, 312);
    
    // 恢复上下文状态
    ctx.restore();
  }



  /**
   * 文字换行处理
   */
  wrapText(ctx, text, maxWidth) {
    const words = text.split('');
    const lines = [];
    let currentLine = '';

    for (let i = 0; i < words.length; i++) {
      const testLine = currentLine + words[i];
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth && currentLine !== '') {
        lines.push(currentLine);
        currentLine = words[i];
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  /**
   * 绘制圆角矩形（兼容性处理）
   */
  drawRoundedRect(ctx, x, y, width, height, radius) {
    if (ctx.roundRect) {
      // 如果支持roundRect方法
      ctx.beginPath();
      ctx.roundRect(x, y, width, height, radius);
    } else {
      // 手动绘制圆角矩形
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    }
  }

  /**
   * 销毁实例，清理资源
   */
  destroy() {
    this.clear();
    this.canvas = null;
    this.ctx = null;
  }
}

/**
 * 便捷方法：直接生成二维码DataURL
 * @param {string} url 要生成二维码的URL
 * @param {Object} options 配置选项
 * @returns {Promise<string>} 二维码图片的DataURL
 */
export async function generateQRCodeDataURL(url, options = {}) {
  const generator = new QRCodeGenerator();
  try {
    await generator.generateQRCode(url, options);
    return generator.toDataURL();
  } finally {
    generator.destroy();
  }
}

/**
 * 便捷方法：直接生成二维码Blob
 * @param {string} url 要生成二维码的URL
 * @param {Object} options 配置选项
 * @returns {Promise<Blob>} 二维码图片的Blob对象
 */
export async function generateQRCodeBlob(url, options = {}) {
  const generator = new QRCodeGenerator();
  try {
    await generator.generateQRCode(url, options);
    return generator.toBlob();
  } finally {
    generator.destroy();
  }
} 